// Owns the legal states and transitions for the browser softphone. SDK,
// websocket, and cross-tab callbacks all use this model so an event for one
// call cannot clear or relabel another call.

import {
    type BrowserVoiceCall,
    type BrowserVoiceDevice,
} from '@apps/web/lib/browser_voice_device_adapter';
import { type InboundBrowserCallTestState } from '@apps/web/lib/inbound_browser_call_test_contract';

export type IncomingBrowserCall = {
    readonly callId: string | null;
    readonly fromNumberDisplay: string;
    readonly callerLabel: string | null;
    readonly lineLabel: string | null;
    readonly ringStartedAt: string | null;
};

type IdleCallLifecycle = {
    readonly kind: 'idle';
    readonly pendingIncomingCall: IncomingBrowserCall | null;
};

type ConnectingOutboundCallLifecycle = {
    readonly kind: 'connecting_outbound';
    readonly attemptId: number;
    readonly caregiverName: string;
    readonly sdkCall: BrowserVoiceCall | null;
    readonly ownedDevice: BrowserVoiceDevice | null;
};

type RingingInboundCallLifecycle = {
    readonly kind: 'ringing_inbound';
    readonly caller: IncomingBrowserCall;
    readonly sdkCall: BrowserVoiceCall;
};

type ActiveCallLifecycle = {
    readonly kind: 'active';
    readonly sdkCall: BrowserVoiceCall;
    readonly displayName: string;
    readonly inboundCallId: string | null;
    readonly ownedDevice: BrowserVoiceDevice | null;
    readonly startedAtMs: number;
    readonly isMuted: boolean;
};

type AnsweredElsewhereCallLifecycle = {
    readonly kind: 'answered_elsewhere';
    readonly caller: IncomingBrowserCall;
    readonly sdkCall: BrowserVoiceCall | null;
    readonly pendingIncomingCall: IncomingBrowserCall | null;
};

export type CallLifecycle =
    | IdleCallLifecycle
    | ConnectingOutboundCallLifecycle
    | RingingInboundCallLifecycle
    | ActiveCallLifecycle
    | AnsweredElsewhereCallLifecycle;

type StartableBrowserCallLifecycle =
    | IdleCallLifecycle
    | (AnsweredElsewhereCallLifecycle & { readonly sdkCall: null });

export type CallLifecycleAction =
    | { readonly type: 'reset' }
    | {
          readonly type: 'incoming_metadata';
          readonly caller: IncomingBrowserCall;
      }
    | {
          readonly type: 'ring_inbound';
          readonly caller: IncomingBrowserCall;
          readonly sdkCall: BrowserVoiceCall;
      }
    | {
          readonly type: 'answered_elsewhere';
          readonly caller: IncomingBrowserCall;
          readonly sdkCall: BrowserVoiceCall | null;
      }
    | {
          readonly type: 'detach_answered_elsewhere_leg';
          readonly sdkCall: BrowserVoiceCall;
      }
    | {
          readonly type: 'start_outbound';
          readonly attemptId: number;
          readonly caregiverName: string;
      }
    | {
          readonly type: 'attach_outbound_device';
          readonly attemptId: number;
          readonly device: BrowserVoiceDevice;
      }
    | {
          readonly type: 'attach_outbound_call';
          readonly attemptId: number;
          readonly sdkCall: BrowserVoiceCall;
      }
    | {
          readonly type: 'activate_call';
          readonly sdkCall: BrowserVoiceCall;
          readonly startedAtMs: number;
      }
    | { readonly type: 'set_muted'; readonly isMuted: boolean };

export type CallLifecycleStore = {
    readonly getSnapshot: () => CallLifecycle;
    readonly subscribe: (listener: () => void) => () => void;
    readonly dispatch: (action: CallLifecycleAction) => void;
};

export const INITIAL_CALL_LIFECYCLE: CallLifecycle = {
    kind: 'idle',
    pendingIncomingCall: null,
};

export function createCallLifecycleStore(): CallLifecycleStore {
    let lifecycle = INITIAL_CALL_LIFECYCLE;
    const listeners = new Set<() => void>();

    return {
        getSnapshot: () => lifecycle,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        dispatch: (action) => {
            const nextLifecycle = reduceCallLifecycle(lifecycle, action);
            if (nextLifecycle === lifecycle) {
                return;
            }
            lifecycle = nextLifecycle;
            listeners.forEach((listener) => listener());
        },
    };
}

export function callIdsMatch(
    firstCallId: string | null,
    secondCallId: string | null,
): boolean {
    // Calls from an older backend can lack the M4 correlation parameter.
    // Keep those calls answerable, but never correlate two missing IDs.
    return firstCallId !== null && firstCallId === secondCallId;
}

export function mergeIncomingCallMetadata(
    immediate: IncomingBrowserCall,
    richer: IncomingBrowserCall | null,
): IncomingBrowserCall {
    if (!richer || !callIdsMatch(immediate.callId, richer.callId)) {
        return immediate;
    }
    return {
        callId: immediate.callId,
        fromNumberDisplay:
            richer.fromNumberDisplay || immediate.fromNumberDisplay,
        callerLabel: richer.callerLabel ?? immediate.callerLabel,
        lineLabel: richer.lineLabel ?? immediate.lineLabel,
        ringStartedAt: richer.ringStartedAt ?? immediate.ringStartedAt,
    };
}

export function currentSdkCall(
    lifecycle: CallLifecycle,
): BrowserVoiceCall | null {
    return lifecycle.kind === 'idle' ? null : lifecycle.sdkCall;
}

export function canStartBrowserCall(
    lifecycle: CallLifecycle,
): lifecycle is StartableBrowserCallLifecycle {
    return (
        lifecycle.kind === 'idle' ||
        (lifecycle.kind === 'answered_elsewhere' && lifecycle.sdkCall === null)
    );
}

export function ownedDevice(
    lifecycle: CallLifecycle,
): BrowserVoiceDevice | null {
    return lifecycle.kind === 'connecting_outbound' ||
        lifecycle.kind === 'active'
        ? lifecycle.ownedDevice
        : null;
}

export type InboundBrowserCallTestProjection = {
    readonly callState: InboundBrowserCallTestState;
    readonly callId: string | null;
};

/**
 * Project the complete lifecycle into the double-gated browser E2E contract.
 *
 * This switch intentionally sits beside the lifecycle union. Adding a new
 * lifecycle kind now requires an explicit decision about its test state and
 * correlation ID instead of silently falling back to idle in CallProvider.
 */
export function projectInboundBrowserCallTestState(
    lifecycle: CallLifecycle,
): InboundBrowserCallTestProjection {
    switch (lifecycle.kind) {
        case 'idle':
            return { callState: 'idle', callId: null };
        case 'connecting_outbound':
            return { callState: 'connecting_outbound', callId: null };
        case 'ringing_inbound':
            return {
                callState: 'incoming',
                callId: lifecycle.caller.callId,
            };
        case 'active':
            return {
                callState: 'active',
                callId: lifecycle.inboundCallId,
            };
        case 'answered_elsewhere':
            return {
                callState: 'answered_elsewhere',
                callId: lifecycle.caller.callId,
            };
        default:
            lifecycle satisfies never;
            throw new Error('Unknown browser call lifecycle');
    }
}

export function reduceCallLifecycle(
    lifecycle: CallLifecycle,
    action: CallLifecycleAction,
): CallLifecycle {
    switch (action.type) {
        case 'reset':
            return INITIAL_CALL_LIFECYCLE;
        case 'incoming_metadata':
            if (lifecycle.kind === 'idle') {
                return {
                    ...lifecycle,
                    pendingIncomingCall: action.caller,
                };
            }
            if (lifecycle.kind === 'ringing_inbound') {
                return callIdsMatch(
                    lifecycle.caller.callId,
                    action.caller.callId,
                )
                    ? {
                          ...lifecycle,
                          caller: mergeIncomingCallMetadata(
                              lifecycle.caller,
                              action.caller,
                          ),
                      }
                    : lifecycle;
            }
            if (lifecycle.kind === 'answered_elsewhere') {
                return callIdsMatch(
                    lifecycle.caller.callId,
                    action.caller.callId,
                )
                    ? {
                          ...lifecycle,
                          caller: mergeIncomingCallMetadata(
                              lifecycle.caller,
                              action.caller,
                          ),
                      }
                    : {
                          ...lifecycle,
                          pendingIncomingCall: action.caller,
                      };
            }
            return lifecycle;
        case 'ring_inbound':
            return {
                kind: 'ringing_inbound',
                caller: action.caller,
                sdkCall: action.sdkCall,
            };
        case 'answered_elsewhere':
            return {
                kind: 'answered_elsewhere',
                caller: action.caller,
                sdkCall: action.sdkCall,
                pendingIncomingCall: null,
            };
        case 'detach_answered_elsewhere_leg':
            return lifecycle.kind === 'answered_elsewhere' &&
                lifecycle.sdkCall === action.sdkCall
                ? { ...lifecycle, sdkCall: null }
                : lifecycle;
        case 'start_outbound':
            return {
                kind: 'connecting_outbound',
                attemptId: action.attemptId,
                caregiverName: action.caregiverName,
                sdkCall: null,
                ownedDevice: null,
            };
        case 'attach_outbound_device':
            return lifecycle.kind === 'connecting_outbound' &&
                lifecycle.attemptId === action.attemptId
                ? { ...lifecycle, ownedDevice: action.device }
                : lifecycle;
        case 'attach_outbound_call':
            return lifecycle.kind === 'connecting_outbound' &&
                lifecycle.attemptId === action.attemptId
                ? { ...lifecycle, sdkCall: action.sdkCall }
                : lifecycle;
        case 'activate_call':
            if (
                lifecycle.kind === 'ringing_inbound' &&
                lifecycle.sdkCall === action.sdkCall
            ) {
                return {
                    kind: 'active',
                    sdkCall: action.sdkCall,
                    displayName:
                        lifecycle.caller.callerLabel ??
                        lifecycle.caller.fromNumberDisplay,
                    inboundCallId: lifecycle.caller.callId,
                    ownedDevice: null,
                    startedAtMs: action.startedAtMs,
                    isMuted: false,
                };
            }
            if (
                lifecycle.kind === 'connecting_outbound' &&
                lifecycle.sdkCall === action.sdkCall
            ) {
                return {
                    kind: 'active',
                    sdkCall: action.sdkCall,
                    displayName: lifecycle.caregiverName,
                    inboundCallId: null,
                    ownedDevice: lifecycle.ownedDevice,
                    startedAtMs: action.startedAtMs,
                    isMuted: false,
                };
            }
            return lifecycle;
        case 'set_muted':
            return lifecycle.kind === 'active'
                ? { ...lifecycle, isMuted: action.isMuted }
                : lifecycle;
        default:
            action satisfies never;
            return lifecycle;
    }
}
